import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './CircularTimer.module.scss';
import { Phrases } from '../../utilities/constants/phrases';

export interface CircularTimerProps {
  duration: number; // in milliseconds
  label?: string;
  size?: number;
  className?: string;
  onSuccess?: () => void;
}

const DEFAULT_DURATION = 5000;
const MIN_SIZE = 12; // px
const STROKE_WIDTH = 2; // px
const MS_PER_SECOND = 1000;
const SVG_ROTATION = -90; // degrees

const CircularTimer: React.FC<CircularTimerProps> = ({
  duration = DEFAULT_DURATION,
  label = Phrases.CIRCULAR_TIMER_LABEL,
  size = 20,
  className,
  onSuccess,
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState(
    Math.ceil(duration / MS_PER_SECOND)
  );
  const timerRef = useRef<NodeJS.Timeout>();
  const successCalledRef = useRef(false);

  const clampedSize = Math.max(MIN_SIZE, size);
  const radius = (clampedSize - STROKE_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
    };

    clearTimer();
    successCalledRef.current = false;

    const totalSeconds = Math.ceil(duration / MS_PER_SECOND);
    setRemainingSeconds(totalSeconds);

    timerRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        const next = prev - 1;
        if (next === 0 && !successCalledRef.current) {
          clearTimer();
          successCalledRef.current = true;
          onSuccess?.();
        }
        return Math.max(0, next);
      });
    }, MS_PER_SECOND);

    return clearTimer;
  }, [duration, onSuccess]);

  return (
    <div
      className={classNames(
        styles.circularTimer,
        'd-flex flex-ai-c gap-xxxs',
        className
      )}>
      <svg
        className={styles.circle}
        width={clampedSize}
        height={clampedSize}
        style={{ transform: `rotate(${SVG_ROTATION}deg)` }}>
        <circle
          cx={clampedSize / 2}
          cy={clampedSize / 2}
          r={radius}
          className={styles.circleTrack}
        />
        <circle
          key={duration}
          cx={clampedSize / 2}
          cy={clampedSize / 2}
          r={radius}
          className={styles.circleProgress}
          strokeDasharray={circumference}
          style={{
            animationDuration: `${duration}ms`,
            ['--circumference' as string]: `${circumference}px`,
          }}
        />
      </svg>
      <span className='text-sm fw-medium'>
        {remainingSeconds} {label}
      </span>
    </div>
  );
};

export default CircularTimer;
